import { ILoginResponse } from '@common/types/user';
import { SplitLayout } from '@components/layout';
import { AuthenticationHeader } from '@components/layout/authentication/AuthenticationHeader';
import { useAuth } from '@hooks/useAuth';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Button, CircularProgress, Typography } from '@mui/material';
import { useGetTwoFARolesQuery, useMigrateUserMutation } from '@services/keycloak';
import { handleError } from '@utils/common';
import { pathToVP } from '@utils/route';
import { isAfter } from 'date-fns';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

const MigrateAccount = () => {
    const { formatMessage } = useIntl();
    const navigate = usePrefixedNavigate();

    const { migrationDate } = useAuth({ disableUserMigrationVerification: true });
    const migrationDeadlinePassed = useMemo(() => isAfter(new Date(), migrationDate), [migrationDate]);

    const { data: twoFARoles } = useGetTwoFARolesQuery();

    const { state } = useLocation();
    const userDetails = (state as { userDetails?: ILoginResponse & { password: string } } | undefined)?.userDetails;

    const [searchParams] = useSearchParams();
    const nextParam = searchParams.get('next');

    useEffect(() => {
        if (!userDetails) {
            const newParams = new URLSearchParams(searchParams);
            navigate({ pathname: '/auth/login', search: newParams.toString() }, { replace: true });
            return;
        }

        window.history.replaceState(
            { ...state, userDetails: undefined },
            document.title,
            window.location.pathname + window.location.search
        );
    }, []);

    const userRoleRequires2FA = useMemo(
        () => userDetails?.role && twoFARoles?.some((role) => role.name === userDetails.role),
        [userDetails, twoFARoles]
    );

    const backToProfUrl = useMemo(() => {
        const newParams = new URLSearchParams(searchParams);

        if (!nextParam || (nextParam && !nextParam.startsWith('/app'))) {
            return pathToVP(
                '',
                Array.from(searchParams.entries()).map(([name, value]) => ({ name, value }))
            ).toString();
        } else {
            newParams.delete('next');
            return `${nextParam}?${newParams.toString()}`;
        }
    }, [searchParams]);

    const [migrateUser, { isLoading: isMigratingUser }] = useMigrateUserMutation();

    const handleAccountMigration = useCallback(() => {
        if (!userDetails) return;
        migrateUser({ password: userDetails?.password })
            .unwrap()
            .then(() => {
                navigate(
                    { pathname: `/auth/migration-success`, search: searchParams.toString() },
                    {
                        state: {
                            'migration-allow': true,
                            twoFAData: { required: userRoleRequires2FA, email: userDetails?.email },
                        },
                    }
                );
            })
            .catch((err) => {
                handleError(formatMessage({ id: 'auth.migrateAccount.error', defaultMessage: 'Migration error' }), err);
            });
    }, [userDetails, userRoleRequires2FA]);

    return (
        <>
            <title>Victron Professional - Account Migration</title>
            <SplitLayout>
                <AuthenticationHeader />
                <Alert severity="info" icon={<InfoOutlinedIcon color="info-main" />}>
                    <Typography fontFamily="Roboto" fontWeight={500} fontSize={16}>
                        <FormattedMessage
                            id="auth.migrateAccount.title"
                            defaultMessage="{brandName} Migrations Have Started"
                            description="Title for account migration process. Used in: /auth/migrate-account"
                            values={{ brandName: 'Victron OneID' }}
                        />
                    </Typography>
                    <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={1}>
                        <FormattedMessage
                            id="auth.migrateAccount.description"
                            defaultMessage="We are making it easier to sign in with {brandName}. Your account will be automatically migrated."
                            description="Explanation of the migration process benefits. Used in: /auth/migrate-account"
                            values={{
                                brandName: (
                                    <Typography component="span" fontWeight={700} fontSize={14}>
                                        Victron OneID
                                    </Typography>
                                ),
                            }}
                        />
                    </Typography>
                    {userRoleRequires2FA ? (
                        <Typography fontFamily="Roboto" fontWeight={400} fontSize={14} mt={2}>
                            <FormattedMessage
                                id="auth.common.twoFactorRequired"
                                defaultMessage="Due to your role 2FA setup will be mandatory."
                                description="Notice that 2FA is required for certain user roles. Used in: /auth/migrate-account and /auth/link-account"
                            />
                        </Typography>
                    ) : null}
                    {!isNaN(migrationDate.getTime()) ? (
                        <Typography fontFamily="Roboto" fontWeight={600} fontSize={14} mt={2} color={'error'}>
                            {migrationDeadlinePassed ? (
                                <FormattedMessage
                                    id="auth.linkAccount.migrationDeadlinePassed"
                                    defaultMessage="The migration deadline for your account has passed. To continue, please migrate your account."
                                    description="Warning when migration deadline has expired. Used in: /auth/link-account and /auth/migrate-account"
                                />
                            ) : (
                                <FormattedMessage
                                    id="auth.linkAccount.migrationDeadline"
                                    defaultMessage="Your account must be migrated by {date}."
                                    description="Notice showing migration deadline date. Used in: /auth/migrate-account"
                                    values={{
                                        date: (
                                            <FormattedDate
                                                value={migrationDate}
                                                year="numeric"
                                                month="long"
                                                day="numeric"
                                            />
                                        ),
                                    }}
                                />
                            )}
                        </Typography>
                    ) : null}
                </Alert>
                <Button
                    className="rounded-full black-border mt-6"
                    fullWidth
                    onClick={handleAccountMigration}
                    startIcon={isMigratingUser ? <CircularProgress size={20} /> : null}
                    disabled={isMigratingUser}
                >
                    <FormattedMessage
                        id="auth.linkAccount.confirmButton"
                        defaultMessage="Confirm Account Migration"
                        description="Text for confirm migration button. Used in: /auth/link-account and /auth/migrate-account"
                    />
                </Button>
                {!migrationDeadlinePassed ? (
                    <Button
                        disabled={isMigratingUser}
                        color="white"
                        className="rounded-full black-border mt-2"
                        fullWidth
                        {...(nextParam?.startsWith('/app')
                            ? { component: Link, to: backToProfUrl }
                            : { href: backToProfUrl })}
                    >
                        <FormattedMessage
                            id="auth.linkAccount.skipButton"
                            defaultMessage="Skip Account Migration"
                            description="Text for skip migration button. Used in: /auth/link-account and /auth/migrate-account"
                        />
                    </Button>
                ) : null}
            </SplitLayout>
        </>
    );
};

export { MigrateAccount };
